(() => {
  if (customElements.get('maytrix-event-slideshow')) return;

  class EventSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.mxev-slideshow-track');
      this.slides = Array.from(this.querySelectorAll('.mxev-slideshow-slide'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-index]'));
      if (!this.track || !this.slides.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.index = 0;
      this.querySelector('.mxev-slideshow-controls').hidden = false;
      this.preload = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
        this.preload.disconnect();
      }, { rootMargin: '300px' });
      this.preload.observe(this);
      this.querySelectorAll('[data-slide-step]').forEach(button => {
        button.addEventListener('click', () => this.goTo(this.index + Number(button.dataset.slideStep)), options);
      });
      this.dots.forEach((button, index) => {
        button.addEventListener('click', () => this.goTo(index), options);
      });
      this.track.addEventListener('keydown', event => {
        const moves = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in moves)) return;
        event.preventDefault();
        this.goTo(moves[event.key]);
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, index: this.index };
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
        event.preventDefault();
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        this.track.scrollLeft = this.drag.left + this.drag.x - event.clientX;
      }, options);
      const finishDrag = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const drag = this.drag;
        this.drag = null;
        const delta = this.track.scrollLeft - drag.left;
        let index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (index === drag.index && Math.abs(delta) > 40) index += Math.sign(delta);
        index = Math.max(0, Math.min(this.slides.length - 1, index));
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        this.goTo(index);
      };
      this.track.addEventListener('pointerup', finishDrag, options);
      this.track.addEventListener('pointercancel', finishDrag, options);
      this.track.addEventListener('lostpointercapture', finishDrag, options);
      this.resize = new ResizeObserver(() => {
        this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'instant' });
      });
      this.resize.observe(this.track);
      this.update();
    }

    goTo(index) {
      const target = (index + this.slides.length) % this.slides.length;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    update() {
      if (!this.track.clientWidth) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
      this.dots.forEach((button, index) => button.setAttribute('aria-current', String(index === this.index)));
      this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== this.index)));
      this.querySelector('[data-slide-status]').textContent = this.slides[this.index].getAttribute('aria-label');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      this.preload?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('maytrix-event-slideshow', EventSlideshow);
})();
